// Shop Economy System
// Repairs and zone economy helpers
//
// NOTE: NPC buy/sell has been replaced by the Order Book Market system.
// This file now only contains repair cost calculation and zone economy types.

import { ALL_EQUIPMENT, Equipment, EquipmentCategory } from './equipment.js';
import { SHIPS, ShipClass } from './ships.js';
import { RESOURCES } from './constants.js';

// ============================================
// TYPES
// ============================================

export type ZoneType = 'green' | 'yellow' | 'red';

export interface ConstellationEconomy {
  tier: number;
  zoneType: ZoneType;
  resourceBonus: string | null;
}

// ============================================
// CONSTANTS
// ============================================

// Repair cost per HP
export const REPAIR_COST_PER_HP = 2;

// ============================================
// REPAIR FUNCTIONS
// ============================================

/**
 * Calculate repair cost for a ship
 */
export function getRepairCost(currentHull: number, maxHull: number): number {
  const damage = maxHull - currentHull;
  return Math.max(0, damage * REPAIR_COST_PER_HP);
}

// ============================================
// ITEM VALUE FUNCTIONS (used by market)
// ============================================

/**
 * Get the base value of an item (used by order book market)
 */
export function getItemBaseValue(itemType: 'equipment' | 'ship' | 'resource', itemId: string): number {
  switch (itemType) {
    case 'equipment':
      return ALL_EQUIPMENT[itemId]?.value ?? 0;
    case 'ship':
      return getShipBasePrice(SHIPS[itemId]);
    case 'resource':
      return (RESOURCES as Record<string, { value: number }>)[itemId]?.value ?? 0;
    default:
      return 0;
  }
}

/**
 * Calculate ship base price from stats
 */
export function getShipBasePrice(shipClass: ShipClass | undefined): number {
  if (!shipClass) return 0;
  const tierMultiplier = Math.pow(shipClass.tier, 2);
  const basePrice = tierMultiplier * 1000;
  const hullValue = shipClass.stats.hull * 10;
  const cargoValue = shipClass.stats.cargoCapacity * 20;
  const slotValue = shipClass.slots.length * 200;
  return basePrice + hullValue + cargoValue + slotValue;
}

// Pre-calculated ship prices
export const SHIP_PRICES: Record<string, number> = Object.fromEntries(
  Object.entries(SHIPS).map(([id, ship]) => [id, getShipBasePrice(ship)])
);

/**
 * Get item name for display
 */
export function getItemName(itemType: 'equipment' | 'ship' | 'resource', itemId: string): string {
  switch (itemType) {
    case 'equipment':
      return ALL_EQUIPMENT[itemId]?.name ?? itemId;
    case 'ship':
      return SHIPS[itemId]?.name ?? itemId;
    case 'resource':
      return (RESOURCES as Record<string, { name: string }>)[itemId]?.name ?? itemId;
    default:
      return itemId;
  }
}

/**
 * Get item tier
 */
export function getItemTier(itemType: 'equipment' | 'ship' | 'resource', itemId: string): number {
  switch (itemType) {
    case 'equipment':
      return ALL_EQUIPMENT[itemId]?.tier ?? 1;
    case 'ship':
      return SHIPS[itemId]?.tier ?? 1;
    case 'resource':
      // Resources: iron=1, copper=2, titanium=3, platinum=4
      const resourceTiers: Record<string, number> = {
        iron: 1,
        copper: 2,
        titanium: 3,
        platinum: 4,
      };
      return resourceTiers[itemId] ?? 1;
    default:
      return 1;
  }
}

/**
 * Get zone description for UI
 */
export function getZoneDescription(zoneType: ZoneType): string {
  switch (zoneType) {
    case 'green':
      return 'Safe Zone: Lower risk, lower rewards';
    case 'yellow':
      return 'Neutral Zone: Moderate risk and rewards';
    case 'red':
      return 'Danger Zone: High risk, high rewards';
  }
}
